import * as Blockly from 'blockly';
import controlDict from './openfoam.controlDict.js';
import fvSchemes from './openfoam.fvSchemes.js';
import fvSolution from './openfoam.fvSolution.js';
import decomposeParDict from './openfoam.decomposeParDict.js';
import common from './openfoam.common.js';

// Registry to store dictionary modules
export const dictRegistry = new Map();

// Populate registry with imported dictionary modules
[
  controlDict,
  fvSchemes,
  fvSolution,
  decomposeParDict,
  common,
].forEach((module) => {
  if (module.dictName) {
    dictRegistry.set(module.dictName, module);
  } else {
    console.warn(`Module missing dictName, skipping:`, module);
  }
});

// Register OpenFOAM blocks for a specific dictionary
export function registerOpenFoamBlocks(dictName) {
  const dictModule = dictRegistry.get(dictName);
  if (!dictModule) {
    console.error(`No module found for dictionary: ${dictName}`);
    return;
  }

  // Register the blocks for this dictionary
  if (typeof dictModule.registerBlocks === 'function') {
    dictModule.registerBlocks();
    console.log(`Registered blocks for ${dictName}`);
  } else {
    console.error(`Module ${dictName} does not have a registerBlocks function`);
  }

  // Always register common blocks as well
  const commonModule = dictRegistry.get('common');
  if (commonModule && typeof commonModule.registerBlocks === 'function') {
    commonModule.registerBlocks();
    console.log(`Registered common blocks`);
  }

  console.log(`Completed OpenFOAM block registration for ${dictName} (plus common) via registry`);
}

// Generate OpenFOAM toolbox XML for a specific dictionary + common
export function generateOpenFoamToolbox(dictName) {
  const dictModule = dictRegistry.get(dictName);
  const commonModule = dictRegistry.get('common');

  if (!dictModule) {
    console.error(`No module found for dictionary: ${dictName}`);
    return '<xml></xml>';
  }

  let combinedXml = '';

  // Append main dict toolbox XML
  combinedXml += dictModule.generateToolbox?.() || '';

  // Append common toolbox XML
  if (commonModule) {
    combinedXml = mergeToolboxes(combinedXml, commonModule.generateToolbox?.() || '');
  }

  return combinedXml;
}

// Get registered OpenFOAM blocks for a specific dictionary
export function getOpenFoamBlocks(dictName) {
  const dictModule = dictRegistry.get(dictName);
  const commonModule = dictRegistry.get('common');

  if (!dictModule) {
    console.error(`No module found for dictionary: ${dictName}`);
    return [];
  }

  const blocks = [...dictModule.getBlocks?.() || []];

  if (commonModule) {
    blocks.push(...commonModule.getBlocks?.() || []);
  }

  return blocks;
}

// Update toolbox XML with OpenFOAM blocks for a specific dictionary + common
export function updateToolboxWithOpenFoamBlocks(inputToolboxXml, dictName) {
  const dictModule = dictRegistry.get(dictName);
  const commonModule = dictRegistry.get('common');

  if (!dictModule) {
    console.error(`No module found for dictionary: ${dictName}`);
    return inputToolboxXml;
  }

  let toolboxDoc;
  const parser = new DOMParser();

  try {
    toolboxDoc = parser.parseFromString(inputToolboxXml, 'text/xml');
    if (toolboxDoc.documentElement.nodeName === 'parsererror') {
      throw new Error('Invalid input toolbox XML');
    }
  } catch (err) {
    console.error('Failed to parse input toolbox XML:', err);
    toolboxDoc = parser.parseFromString('<xml></xml>', 'text/xml');
  }

  const addToolboxXml = (xmlStr, label) => {
    if (!xmlStr) return;
    try {
      const doc = parser.parseFromString(xmlStr, 'text/xml');
      if (doc.documentElement.nodeName === 'parsererror') throw new Error();
      Array.from(doc.documentElement.children).forEach((el) => {
        toolboxDoc.documentElement.appendChild(toolboxDoc.importNode(el, true));
      });
    } catch {
      console.error(`Failed to parse ${label} toolbox XML.`);
    }
  };

  addToolboxXml(dictModule.generateToolbox?.(), dictName);
  if (commonModule) {
    addToolboxXml(commonModule.generateToolbox?.(), 'common');
  }

  return new XMLSerializer().serializeToString(toolboxDoc);
}

// Utility to merge two toolbox XML strings into one
function mergeToolboxes(xmlA, xmlB) {
  const parser = new DOMParser();
  const toolboxA = parser.parseFromString(xmlA || '<xml></xml>', 'text/xml');
  const toolboxB = parser.parseFromString(xmlB || '<xml></xml>', 'text/xml');

  Array.from(toolboxB.documentElement.children).forEach((el) => {
    toolboxA.documentElement.appendChild(toolboxA.importNode(el, true));
  });

  return new XMLSerializer().serializeToString(toolboxA);
}

// Register blocks for a specific dictionary
export function initializeOpenFoamBlocks(dictName) {
  registerOpenFoamBlocks(dictName);
}
